import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Auth, Socket, Theme & Notifications
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SocketProvider } from "@/contexts/SocketContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { PlatformBroadcastProvider } from "@/contexts/PlatformBroadcastContext";
import PlatformBroadcastPopup from "@/components/platformBroadcast/PlatformBroadcastPopup";
import { ActiveCallProvider } from "@/contexts/ActiveCallContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SupportChatProvider } from "@/contexts/SupportChatContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import NotFoundRedirect from "@/components/NotFoundRedirect";
import RoleGuardedRoute from "@/components/RoleGuardedRoute";
import PermissionRequestGate from "@/components/PermissionRequestGate";
import FeaturePageGate from "@/components/FeaturePageGate";
import { APP_CONNECT_FEATURE_KEY } from "@touchworks/shared/features.js";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

// Internal Pages (eager — lightweight / always needed)
import Dashboard from "./pages/internal/Dashboard";
import Login from "./pages/internal/Login";
import VerifyEmail from "./pages/internal/VerifyEmail";
import ForgotPassword from "./pages/internal/ForgotPassword";
import ResetPassword from "./pages/internal/ResetPassword";
import TrelloCallback from "./pages/auth/trello-callback";

// Implementation Pages (eager — light pages used as defaults/redirects)
import ImplementationEntry from "./pages/internal/implementation/ImplementationEntry";
import EmbedEntry from "./pages/embed/EmbedEntry";
import { BrandProvider } from "@/contexts/BrandContext";
import HomePage from "./pages/internal/home/HomePage";
import LegacyAccountRouteRedirect, {
  LegacyPlatformRouteRedirect,
} from "./components/LegacyAccountRouteRedirect";

// ─── Lazy-loaded heavy pages ───
// Code-splitting dá ao React um boundary de Suspense para que a navegação
// entre telas pesadas (FlowMapper, Inbox, Reports etc.) seja instânea
// e não bloqueie o thread principal durante mount/unmount.
const TouchBuilderHub = lazy(() =>
  import("./pages/internal/implementation/TouchBuilderHub"),
);
const AutomationEditorWrapper = lazy(() =>
  import("./pages/internal/implementation/AutomationEditorWrapper"),
);
const IntegrationsHub = lazy(() =>
  import("./pages/internal/implementation/IntegrationsHub"),
);
const MeetingTracker = lazy(() =>
  import("./pages/internal/implementation/MeetingTracker"),
);
const FlowMapper = lazy(() =>
  import("./pages/internal/implementation/FlowMapper"),
);
const InboxPage = lazy(() => import("./pages/internal/inbox/InboxPage"));
const BroadcastPage = lazy(() =>
  import("./pages/internal/inbox/BroadcastPage"),
);
const CRMPage = lazy(() => import("./pages/internal/implementation/CRMPage"));
const ReportsPage = lazy(() =>
  import("./pages/internal/implementation/ReportsPage"),
);
const PlanningPage = lazy(() =>
  import("./pages/internal/implementation/PlanningPage"),
);
const KnowledgeBase = lazy(() =>
  import("./pages/internal/implementation/KnowledgeBase"),
);
const WidgetConfigPage = lazy(() =>
  import("./pages/internal/implementation/WidgetConfigPage"),
);
const SupportCenter = lazy(() =>
  import("./pages/internal/implementation/SupportCenter"),
);
const WhatsAppTemplatesPage = lazy(() =>
  import("./pages/internal/implementation/WhatsAppTemplatesPage"),
);
const TemplateEditorPage = lazy(() =>
  import("./pages/internal/implementation/TemplateEditorPage"),
);

const ReferralsPage = lazy(() => import("./pages/internal/referrals/ReferralsPage"));
const NotificationsPage = lazy(() =>
  import("./pages/internal/implementation/NotificationsPage"),
);

// Users & Departments
const UsersPage = lazy(() =>
  import("./pages/internal/implementation/UsersPage"),
);
const DepartmentsPage = lazy(() =>
  import("./pages/internal/implementation/DepartmentsPage"),
);
const UserProfilePage = lazy(() =>
  import("./pages/internal/implementation/UserProfilePage"),
);
// Contas vinculadas da conta parceira. Fora do bloco /embed/* de propósito:
// o shell embutido é o que o parceiro mostra às próprias filhas, e uma filha
// não pode ver as irmãs (`embed: false` no catálogo de navegação).
const ChildAccountsPage = lazy(() =>
  import("./pages/internal/implementation/ChildAccountsPage"),
);

// Admin Pages
const PlansPage = lazy(() => import("./pages/internal/admin/PlansPage"));
const AuditPage = lazy(() => import("./pages/internal/admin/AuditPage"));
const BillingPage = lazy(() => import("./pages/internal/admin/BillingPage"));
const ContentManagerPage = lazy(() =>
  import("./pages/internal/admin/ContentManagerPage"),
);
const LucasFeedbackPage = lazy(() =>
  import("./pages/internal/admin/LucasFeedbackPage"),
);
const LucasSchedulePage = lazy(() =>
  import("./pages/internal/admin/LucasSchedulePage"),
);
const LegalDocumentsPage = lazy(() =>
  import("./pages/internal/admin/LegalDocumentsPage"),
);
const PlatformBroadcastsPage = lazy(() =>
  import("./pages/internal/admin/PlatformBroadcastsPage"),
);
const PlatformBroadcastFormPage = lazy(() =>
  import("./pages/internal/admin/PlatformBroadcastFormPage"),
);
const PlatformBroadcastSettingsPage = lazy(() =>
  import("./pages/internal/admin/PlatformBroadcastSettingsPage"),
);
const PlatformBroadcastViewerPage = lazy(() =>
  import("./pages/internal/PlatformBroadcastViewerPage"),
);
const DeliveryDocumentsPage = lazy(() =>
  import("./pages/internal/admin/DeliveryDocumentsPage"),
);
const PartnersPage = lazy(() => import("./pages/internal/platform/PartnersPage"));
const PartnerChildAccountsPage = lazy(() =>
  import("./pages/internal/platform/PartnerChildAccountsPage"),
);
const AgentMetrics = lazy(() => import("./pages/internal/AgentMetrics"));

// Structured Automations
const LegalReportConfig = lazy(() =>
  import("./pages/internal/StructuredAutomations/LegalReport/Config"),
);
const BirthdayConfig = lazy(() =>
  import("./pages/internal/StructuredAutomations/Birthday/Config"),
);
const TaskConfig = lazy(() =>
  import("./pages/internal/StructuredAutomations/Task/Config"),
);
const FazTaskConfig = lazy(() =>
  import("./pages/internal/StructuredAutomations/FazTask/Config"),
);
const SuperlogicaInadimplenciaConfig = lazy(() =>
  import("./pages/internal/StructuredAutomations/SuperlogicaInadimplencia/Config"),
);

// Integrations Detail
const AdvboxDetail = lazy(() =>
  import("./pages/internal/Integrations/AdvboxDetail"),
);
const TrelloDetail = lazy(() =>
  import("./pages/internal/Integrations/TrelloDetail"),
);
const ProjurisDetail = lazy(() =>
  import("./pages/internal/Integrations/ProjurisDetail"),
);
const ControlJusDetail = lazy(() =>
  import("./pages/internal/Integrations/ControlJusDetail"),
);
const FazDetail = lazy(() =>
  import("./pages/internal/Integrations/FazDetail"),
);
const SuperlogicaDetail = lazy(() =>
  import("./pages/internal/Integrations/SuperlogicaDetail"),
);
const AnthropicDetail = lazy(() =>
  import("./pages/internal/Integrations/AnthropicDetail"),
);
const AppClientDetail = lazy(() =>
  import("./pages/internal/appClient/AppClientDetail"),
);

// Sales Intelligence
const SalesIntelligencePage = lazy(() =>
  import("./pages/internal/SalesIntelligence/SalesIntelligencePage"),
);

// Layouts
import InternalLayout from "./layouts/InternalLayout";

/**
 * SmartDashboardRedirect: Sends non-superadmins directly to their implementation page.
 * Superadmins see the global Dashboard.
 */
function SmartDashboardRedirect() {
  const { user, isSuperAdmin, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isSuperAdmin() && user?.accountId) {
    return (
      <Navigate to={`/internal/implementation/${user.accountId}`} replace />
    );
  }
  return <Dashboard />;
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark">
    <AuthProvider>
      <SocketProvider>
        <ActiveCallProvider>
        <BrowserRouter>
          <NotificationProvider>
            <PlatformBroadcastProvider>
            <Toaster position="top-right" />
            <PlatformBroadcastPopup />
            <ErrorBoundary inline>
            <Routes>
              {/* Redirect Root to Internal */}
              <Route
                path="/"
                element={<Navigate to="/internal/dashboard" replace />}
              />

              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/trello/callback" element={<TrelloCallback />} />

              {/* Protected Internal Routes */}
              <Route
                path="/internal"
                element={
                  <ProtectedRoute>
                    {/* Acima do layout porque o botão "Suporte" vive na sidebar
                        e a conversa vive na home — um precisa avisar o outro. */}
                    <SupportChatProvider>
                      <InternalLayout />
                    </SupportChatProvider>
                  </ProtectedRoute>
                }
              >
                <Route path="" element={<SmartDashboardRedirect />} />
                <Route path="dashboard" element={<SmartDashboardRedirect />} />

                {/* Support Center — account-independent, shareable URLs */}
                <Route path="support" element={<SupportCenter />} />
                <Route path="support/article/:articleId" element={<SupportCenter />} />

                {/* Indique e Ganhe — fora do /embed de proposito: uma conta
                    filha de parceiro nao indica ninguem (ela nem passa pelo
                    checkout do site, que e onde o cookie do link e lido). */}
                <Route
                  path="indicacoes"
                  element={<RoleGuardedRoute minLevel={30}><ReferralsPage /></RoleGuardedRoute>}
                />

                {/* Implementation - Main Nav.
                    The bare path has no :clientId, so the page falls back to
                    the account on the session rather than fetching `undefined`
                    like the legacy dashboard did here. */}
                <Route path="implementation" element={<HomePage />} />
                <Route
                  path="implementation/:clientId"
                  element={<ImplementationEntry />}
                />
                <Route
                  path="implementation/:clientId/inbox"
                  element={<RoleGuardedRoute minLevel={10}><InboxPage /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/crm"
                  element={
                    <PermissionRequestGate permission="crm.read" featureLabel="Kanban / CRM">
                      <FeaturePageGate featureKey="crm.enabled" featureName="Kanban / CRM">
                        <CRMPage />
                      </FeaturePageGate>
                    </PermissionRequestGate>
                  }
                />
                <Route
                  path="implementation/:clientId/reports"
                  element={<RoleGuardedRoute minLevel={30}><ReportsPage /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/planning"
                  element={<RoleGuardedRoute minLevel={50}><PlanningPage /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/knowledge-base"
                  element={
                    <RoleGuardedRoute minLevel={10}>
                      <FeaturePageGate featureKey="rag.enabled" featureName="Base de Conhecimento">
                        <KnowledgeBase />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                {/* T30, EMB-22 — só admins de contas parceiras chegam aqui;
                    FeaturePageGate é o que de fato barra quem não tem
                    embed.partner (o backend confere de novo, embedPartnerGate). */}
                <Route
                  path="implementation/:clientId/widget-config"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="embed.partner" featureName="Widget de chat">
                        <WidgetConfigPage />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/support"
                  element={<SupportCenter />}
                />
                <Route
                  path="implementation/:clientId/notifications"
                  element={<NotificationsPage />}
                />

                {/* Implementation - Core */}
                <Route
                  path="implementation/:clientId/automation"
                  element={<RoleGuardedRoute minLevel={50}><TouchBuilderHub /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/flow/:flowId"
                  element={<AutomationEditorWrapper />}
                />
                <Route
                  path="implementation/:clientId/broadcasts"
                  element={<RoleGuardedRoute minLevel={30}><BroadcastPage /></RoleGuardedRoute>}
                />

                {/* Implementation - Config (in popover) */}
                <Route
                  path="implementation/:clientId/channels"
                  element={<RoleGuardedRoute minLevel={50}><IntegrationsHub /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/integrations"
                  element={<RoleGuardedRoute minLevel={50}><IntegrationsHub /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/whatsapp-templates"
                  element={<WhatsAppTemplatesPage />}
                />
                <Route
                  path="implementation/:clientId/whatsapp-templates/new"
                  element={<TemplateEditorPage />}
                />
                <Route
                  path="implementation/:clientId/whatsapp-templates/:templateId/edit"
                  element={<TemplateEditorPage />}
                />

                {/* Advbox Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/advbox"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.advbox" featureName="AdvBox">
                        <AdvboxDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* Trello Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/trello"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.trello" featureName="Trello">
                        <TrelloDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* Projuris Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/projuris"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.projuris" featureName="Projuris">
                        <ProjurisDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* ControlJUS Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/controljus"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.controljus" featureName="ControlJUS">
                        <ControlJusDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* Processo Já — branding, preview, adoption metrics.
                    Gated on the same plan feature as the app_connect channel dialog
                    (routes/internal/appConnect.js) — one entitlement, two surfaces. */}
                <Route
                  path="implementation/:clientId/integrations/touch-connect"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey={APP_CONNECT_FEATURE_KEY} featureName="App do Cliente">
                        <AppClientDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* SEDEP FAZ Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/faz"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FazDetail />
                    </RoleGuardedRoute>
                  }
                />

                {/* Superlógica Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/superlogica"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <SuperlogicaDetail />
                    </RoleGuardedRoute>
                  }
                />

                {/* Anthropic (Claude BYOK) Integration Detail */}
                <Route
                  path="implementation/:clientId/integrations/anthropic"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <AnthropicDetail />
                    </RoleGuardedRoute>
                  }
                />

                {/* Structured Automations */}
                <Route
                  path="implementation/:clientId/structured-automations/legal-report/new"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <LegalReportConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/legal-report/:id"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <LegalReportConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/birthday/new"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <BirthdayConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/birthday/:id"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <BirthdayConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/task/new"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <TaskConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/task/:id"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <TaskConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/faz-task/new"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FazTaskConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/faz-task/:id"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FazTaskConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/superlogica-inadimplencia/new"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <SuperlogicaInadimplenciaConfig />
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="implementation/:clientId/structured-automations/superlogica-inadimplencia/:id"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <SuperlogicaInadimplenciaConfig />
                    </RoleGuardedRoute>
                  }
                />

                {/* Sales Intelligence */}
                <Route
                  path="implementation/:clientId/sales-intelligence"
                  element={
                    <RoleGuardedRoute minLevel={30}>
                      <FeaturePageGate featureKey="sales.enabled" featureName="Sales Intelligence">
                        <SalesIntelligencePage />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />

                {/* Legacy routes (redirect or keep for compatibility) */}
                <Route
                  path="implementation/:clientId/builder"
                  element={<TouchBuilderHub />}
                />
                <Route
                  path="implementation/:clientId/meetings"
                  element={<MeetingTracker />}
                />
                <Route
                  path="implementation/:clientId/flowmapper"
                  element={<FlowMapper />}
                />

                {/* Users & Departments */}
                <Route
                  path="implementation/:clientId/users"
                  element={<RoleGuardedRoute minLevel={50}><UsersPage /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/departments"
                  element={<RoleGuardedRoute minLevel={50}><DepartmentsPage /></RoleGuardedRoute>}
                />
                <Route
                  path="implementation/:clientId/contas"
                  element={<RoleGuardedRoute minLevel={50}><ChildAccountsPage /></RoleGuardedRoute>}
                />

                {/* User Profile & Settings */}
                <Route
                  path="implementation/:clientId/profile"
                  element={<UserProfilePage />}
                />

                {/* ── Telas DA CONTA — o :clientId na URL é o escopo ──────────
                    Antes viviam sob `admin/`, misturadas com o CMS da
                    plataforma e sem conta na URL: o contexto só existia no JWT,
                    então recarregar ou copiar o link perdia de vista em qual
                    conta você estava. */}
                <Route
                  path="implementation/:clientId/admin/billing"
                  element={<BillingPage />}
                />
                <Route
                  path="implementation/:clientId/admin/audit"
                  element={<AuditPage />}
                />

                {/* ── Telas DA PLATAFORMA — superadmin, sem conta por natureza ──
                    Operam sobre todas as contas (planos, conteúdo editorial,
                    comunicados) ou escolhem a conta dentro da própria tela
                    (delivery-documents, agent-metrics — esta filtra por
                    `facets.accounts`, é cross-account). */}
                <Route path="platform/agent-metrics" element={<AgentMetrics />} />
                <Route path="platform/plans" element={<PlansPage />} />
                <Route path="platform/content" element={<ContentManagerPage />} />
                <Route path="platform/avaliacoes-lucas" element={<LucasFeedbackPage />} />
                <Route path="platform/atendimento-lucas" element={<LucasSchedulePage />} />
                <Route path="platform/legal-documents" element={<LegalDocumentsPage />} />
                <Route path="platform/comunicados" element={<PlatformBroadcastsPage />} />
                <Route path="platform/comunicados/novo" element={<PlatformBroadcastFormPage />} />
                <Route
                  path="platform/comunicados/:id/editar"
                  element={<PlatformBroadcastFormPage />}
                />
                <Route
                  path="platform/comunicados/configuracoes"
                  element={<PlatformBroadcastSettingsPage />}
                />
                <Route
                  path="platform/delivery-documents"
                  element={<DeliveryDocumentsPage />}
                />
                <Route path="platform/parceiros" element={<PartnersPage />} />
                <Route
                  path="platform/parceiros/:partnerId/contas"
                  element={<PartnerChildAccountsPage />}
                />

                {/* Leitura de comunicado pelo usuário final — independente de conta. */}
                <Route path="comunicados/:id" element={<PlatformBroadcastViewerPage />} />

                {/* ── Compatibilidade: rotas antigas sob `admin/` ─────────────
                    Mantidas como redirect para não quebrar link salvo, e-mail
                    enviado ou notificação já entregue. */}
                <Route
                  path="admin/billing"
                  element={<LegacyAccountRouteRedirect to="admin/billing" />}
                />
                <Route
                  path="admin/audit"
                  element={<LegacyAccountRouteRedirect to="admin/audit" />}
                />
                <Route
                  path="admin/agent-metrics"
                  element={<Navigate to="/internal/platform/agent-metrics" replace />}
                />
                <Route
                  path="admin/plans"
                  element={<Navigate to="/internal/platform/plans" replace />}
                />
                <Route
                  path="admin/content"
                  element={<Navigate to="/internal/platform/content" replace />}
                />
                <Route
                  path="admin/legal-documents"
                  element={<Navigate to="/internal/platform/legal-documents" replace />}
                />
                <Route
                  path="admin/delivery-documents"
                  element={<Navigate to="/internal/platform/delivery-documents" replace />}
                />
                <Route
                  path="admin/comunicados/novo"
                  element={<Navigate to="/internal/platform/comunicados/novo" replace />}
                />
                <Route
                  path="admin/comunicados/configuracoes"
                  element={
                    <Navigate to="/internal/platform/comunicados/configuracoes" replace />
                  }
                />
                <Route
                  path="admin/comunicados/:id/editar"
                  element={
                    <LegacyPlatformRouteRedirect
                      build={({ id }) => `/internal/platform/comunicados/${id}/editar`}
                    />
                  }
                />
                <Route
                  path="admin/comunicados"
                  element={<Navigate to="/internal/platform/comunicados" replace />}
                />
              </Route>

              {/* ── Shell embutido (EMB-11) ──────────────────────────────────
                  Mesmos componentes de página que /internal/implementation/:clientId/*
                  — nenhum arquivo de tela é duplicado. Duas diferenças
                  deliberadas em relação à árvore acima:
                  (1) SEM :clientId na URL — a conta vem do bearer da sessão
                      embutida (T8/T13), nunca da URL (ver embedPath());
                  (2) SEM ProtectedRoute — ele redireciona para /login, que o
                      shell embutido nunca pode mostrar (EMB-13 AC8).
                      EmbedEntry (T17) é o gate real: mostra a tela de pouso
                      enquanto não há sessão, pede um token ao host via
                      EmbedContext (T12), e cai na tela de orientação
                      (EmbedExpired) se o host nunca responder — nunca a
                      tela de login. Reaproveita RoleGuardedRoute/
                      FeaturePageGate/PermissionRequestGate tal como estão.
                  Todas as sub-rotas de segundo nível estão espelhadas aqui
                  (PLAN_embed-account-scope.md, fases 1 e 2) — flow/:flowId,
                  whatsapp-templates, detalhes de integração e
                  structured-automations/legal-report. Cada página resolve a
                  própria conta via useAccountScope() em vez de :clientId.
                  Fora do escopo, de propósito: contas-filhas do parceiro
                  (`contas`, embed:false — ver navigation.js), admin/billing,
                  admin/audit e as rotas legadas de compatibilidade
                  (builder/meetings/flowmapper). */}
              <Route
                path="/embed"
                element={
                  <EmbedEntry>
                    <BrandProvider>
                      <SupportChatProvider>
                        <InternalLayout />
                      </SupportChatProvider>
                    </BrandProvider>
                  </EmbedEntry>
                }
              >
                <Route index element={<ImplementationEntry />} />
                <Route path="dashboard" element={<ImplementationEntry />} />
                <Route
                  path="inbox"
                  element={<RoleGuardedRoute minLevel={10}><InboxPage /></RoleGuardedRoute>}
                />
                <Route
                  path="crm"
                  element={
                    <PermissionRequestGate permission="crm.read" featureLabel="Kanban / CRM">
                      <FeaturePageGate featureKey="crm.enabled" featureName="Kanban / CRM">
                        <CRMPage />
                      </FeaturePageGate>
                    </PermissionRequestGate>
                  }
                />
                <Route
                  path="reports"
                  element={<RoleGuardedRoute minLevel={30}><ReportsPage /></RoleGuardedRoute>}
                />
                <Route
                  path="knowledge-base"
                  element={
                    <RoleGuardedRoute minLevel={10}>
                      <FeaturePageGate featureKey="rag.enabled" featureName="Base de Conhecimento">
                        <KnowledgeBase />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route path="support" element={<SupportCenter />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route
                  path="automation"
                  element={<RoleGuardedRoute minLevel={50}><TouchBuilderHub /></RoleGuardedRoute>}
                />
                {/* PLAN_embed-account-scope.md fase 1 — sem esta rota, o clique
                    num fluxo em TouchBuilderHub saía do shell embutido para
                    /internal/implementation/undefined/flow/:flowId (404 →
                    catch-all → /internal/dashboard, fora do iframe). */}
                <Route
                  path="flow/:flowId"
                  element={<AutomationEditorWrapper />}
                />
                <Route
                  path="broadcasts"
                  element={<RoleGuardedRoute minLevel={30}><BroadcastPage /></RoleGuardedRoute>}
                />
                <Route
                  path="channels"
                  element={<RoleGuardedRoute minLevel={50}><IntegrationsHub /></RoleGuardedRoute>}
                />
                <Route
                  path="integrations"
                  element={<RoleGuardedRoute minLevel={50}><IntegrationsHub /></RoleGuardedRoute>}
                />
                {/* PLAN_embed-account-scope.md fase 2 — segundo nível de
                    Integrações/Automações, alcançável a partir dos cards de
                    IntegrationsHub/TouchBuilderHub (ambos já usam
                    scopedPath()). Mesmos componentes e gates do bloco
                    implementation/:clientId/* acima — cada página já resolve
                    a própria conta via useAccountScope(). */}
                <Route
                  path="whatsapp-templates"
                  element={<WhatsAppTemplatesPage />}
                />
                <Route
                  path="whatsapp-templates/new"
                  element={<TemplateEditorPage />}
                />
                <Route
                  path="whatsapp-templates/:templateId/edit"
                  element={<TemplateEditorPage />}
                />
                <Route
                  path="integrations/advbox"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.advbox" featureName="AdvBox">
                        <AdvboxDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="integrations/trello"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.trello" featureName="Trello">
                        <TrelloDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="integrations/projuris"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.projuris" featureName="Projuris">
                        <ProjurisDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="integrations/controljus"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey="integrations.controljus" featureName="ControlJUS">
                        <ControlJusDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="integrations/touch-connect"
                  element={
                    <RoleGuardedRoute minLevel={50}>
                      <FeaturePageGate featureKey={APP_CONNECT_FEATURE_KEY} featureName="App do Cliente">
                        <AppClientDetail />
                      </FeaturePageGate>
                    </RoleGuardedRoute>
                  }
                />
                <Route
                  path="integrations/faz"
                  element={<RoleGuardedRoute minLevel={50}><FazDetail /></RoleGuardedRoute>}
                />
                <Route
                  path="integrations/superlogica"
                  element={<RoleGuardedRoute minLevel={50}><SuperlogicaDetail /></RoleGuardedRoute>}
                />
                <Route
                  path="integrations/anthropic"
                  element={<RoleGuardedRoute minLevel={50}><AnthropicDetail /></RoleGuardedRoute>}
                />
                <Route
                  path="structured-automations/legal-report/new"
                  element={<RoleGuardedRoute minLevel={50}><LegalReportConfig /></RoleGuardedRoute>}
                />
                <Route
                  path="structured-automations/legal-report/:id"
                  element={<RoleGuardedRoute minLevel={50}><LegalReportConfig /></RoleGuardedRoute>}
                />
                <Route
                  path="users"
                  element={<RoleGuardedRoute minLevel={50}><UsersPage /></RoleGuardedRoute>}
                />
                <Route
                  path="departments"
                  element={<RoleGuardedRoute minLevel={50}><DepartmentsPage /></RoleGuardedRoute>}
                />
                {/* Fora da lista de nav do brief, mas alcançável pelo menu do
                    avatar ("Meu Perfil") — precisa existir para o link não
                    quebrar. T18 esconde a seção Segurança aqui em modo embed. */}
                <Route path="profile" element={<UserProfilePage />} />
              </Route>

              {/* Catch all — embed-aware (PLAN_embed-account-scope.md, task 8):
                  this route is a SIBLING of the /embed subtree above, so it
                  can't read useEmbed(); NotFoundRedirect checks the path
                  directly instead, so an unmatched /embed/* URL stays inside
                  the shell instead of ejecting to /internal/dashboard. */}
              <Route path="*" element={<NotFoundRedirect />} />
            </Routes>
            </ErrorBoundary>
            </PlatformBroadcastProvider>
          </NotificationProvider>
        </BrowserRouter>
        </ActiveCallProvider>
      </SocketProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
